import React from "react";
import LockPersonIcon from "@mui/icons-material/LockPerson";
import PeopleIcon from "@mui/icons-material/People";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SpeedIcon from "@mui/icons-material/Speed";
import PieChartIcon from "@mui/icons-material/PieChart";
import CasesIcon from "@mui/icons-material/Cases";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DataExplorationIcon from "@mui/icons-material/DataExploration";
import FlareIcon from "@mui/icons-material/Flare";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import BiotechIcon from "@mui/icons-material/Biotech";
import ShowChartIcon from "@mui/icons-material/ShowChart";

// Route configuration object that maps route paths to navigation display names
export const ROUTE_CONFIG = {
  dashboard: "DASHBOARD",
  portfolio: "PORTFOLIO",
  oiSpurts: "OI SPURTS",
  tableAnalysis: "ANALYSIS",
  compAnalysis: "COMP ANALYSIS",
  pieChart: "PIE CHART",
  oi: "OI",
  adv_dec: "ADV/DEC",
  marketView: "MARKET VIEW",
  sectorMomentum: "SECTOR MOMENTUM",
  scoreMomentum: "SCORE MOMENTUM",
  screenerTrends: "SCREENER TRENDS",
  research: "RESEARCH",
  smallCase: "SMALL CASE",
  smallCasePerformance: "PERFORMANCE",
  subscribers: "SUBSCRIBERS",
  excelUpdate: "EXCEL UPDATE",
  adminPanel: "ADMIN PANEL",
} as const;

// Route configuration with icons for SideNavBar
export const ROUTE_CONFIG_WITH_ICONS: Record<
  string,
  { nav: string; icon: React.ReactNode }
> = {
  dashboard: {
    nav: "DASHBOARD",
    icon: <DashboardIcon />,
  },
  portfolio: {
    nav: "PORTFOLIO",
    icon: <AttachMoneyIcon />,
  },
  oiSpurts: {
    nav: "OI SPURTS",
    icon: <SpeedIcon />,
  },
  tableAnalysis: {
    nav: "ANALYSIS",
    icon: <AnalyticsIcon />,
  },
  compAnalysis: {
    nav: "COMP ANALYSIS",
    icon: <AutoGraphIcon />,
  },
  pieChart: {
    nav: "PIE CHART",
    icon: <PieChartIcon />,
  },
  oi: {
    nav: "OI",
    icon: <CandlestickChartIcon />,
  },
  adv_dec: {
    nav: "ADV/DEC",
    icon: <BackupTableIcon />,
  },
  marketView: {
    nav: "MARKET VIEW",
    icon: <AutoGraphIcon />,
  },
  sectorMomentum: {
    nav: "SECTOR MOMENTUM",
    icon: <DataExplorationIcon />,
  },
  scoreMomentum: {
    nav: "SCORE MOMENTUM",
    icon: <FlareIcon />,
  },
  screenerTrends: {
    nav: "SCREENER TRENDS",
    icon: <ShowChartIcon />,
  },
  research: {
    nav: "RESEARCH",
    icon: <BiotechIcon />,
  },
  smallCase: {
    nav: "SMALL CASE",
    icon: <CasesIcon />,
  },
  smallCasePerformance: {
    nav: "PERFORMANCE",
    icon: <SsidChartIcon />,
  },
  subscribers: {
    nav: "SUBSCRIBERS",
    icon: <PeopleIcon />,
  },
  excelUpdate: {
    nav: "EXCEL UPDATE",
    icon: <UploadFileIcon />,
  },
  adminPanel: {
    nav: "ADMIN PANEL",
    icon: <LockPersonIcon />,
  },
};

// Available routes (excluding nested routes like research/createCall, research/updateCall/:id, subscribers/:id)
export const AVAILABLE_ROUTES = [
  "dashboard",
  "portfolio",
  "oiSpurts",
  "tableAnalysis",
  "compAnalysis",
  "pieChart",
  "oi",
  "adv_dec",
  "marketView",
  "sectorMomentum",
  "scoreMomentum",
  "screenerTrends",
  "research",
  "smallCase",
  "smallCasePerformance",
  "subscribers",
  "excelUpdate",
  "adminPanel",
] as const;

// Function to generate NAV_LIST dynamically based on available routes
export const generateNavList = () => {
  return AVAILABLE_ROUTES.filter(
    (route) => ROUTE_CONFIG[route as keyof typeof ROUTE_CONFIG]
  ) // Only include routes that have a display name
    .map((route) => ({
      nav: ROUTE_CONFIG[route as keyof typeof ROUTE_CONFIG],
      path: route,
    }));
};

// Function to generate NAV_LIST dynamically with icons and dividers for SideNavBar
export const generateNavListWithIcons = () => {
  const navList: Array<
    | { type: "nav"; nav: string; path: string; icon: React.ReactNode }
    | { type: "divider" }
  > = [];

  AVAILABLE_ROUTES.forEach((route) => {
    const config = ROUTE_CONFIG_WITH_ICONS[route];
    if (config) {
      navList.push({
        type: "nav",
        nav: config.nav,
        path: route,
        icon: config.icon,
      });

      // Add dividers after specific sections
      if (
        route === "scoreMomentum" ||
        route === "research" ||
        route === "subscribers"
      ) {
        navList.push({ type: "divider" });
      }
    }
  });

  return navList;
};
